'use client'

import React, { useEffect, useMemo, useRef, useState } from 'react'
import { programData } from '@/app/wise/ProgramData'

const getPageSize = (width) => {
  if (width >= 1200) return 12
  if (width >= 992) return 12
  if (width >= 768) return 8
  return 4
}

const ProgramsSpecializations = () => {
  const programs = useMemo(() => Object.keys(programData || {}), [])

  const [activeIndex, setActiveIndex] = useState(0)
  const [coursePage, setCoursePage] = useState(0)
  const [pageSize, setPageSize] = useState(() =>
    typeof window === 'undefined' ? 12 : getPageSize(window.innerWidth)
  )

  const programBtnRefs = useRef([])
  const scrollContainerRef = useRef(null)

  const activeKey = programs[activeIndex]
  const activeProgram = activeKey ? programData[activeKey] : null
  const courses = activeProgram?.courses || []

  const maxCoursePage = Math.max(0, Math.ceil(courses.length / pageSize) - 1)
  const visibleCourses = courses.slice(coursePage * pageSize, coursePage * pageSize + pageSize)

  // Resize: recalc pageSize
  useEffect(() => {
    const onResize = () => setPageSize(getPageSize(window.innerWidth))
    onResize()
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  // Reset course page when program or page size changes
  useEffect(() => {
    setCoursePage(0)
  }, [activeIndex, pageSize])

  // ✅ Keep active program button visible horizontally (no page scroll)
  useEffect(() => {
    const container = scrollContainerRef.current
    const btn = programBtnRefs.current[activeIndex]
    if (!container || !btn) return

    // avoid synchronous layout reads; use rAF and scrollIntoView
    requestAnimationFrame(() => {
      btn.scrollIntoView({ inline: 'center', behavior: 'smooth' })
    })
  }, [activeIndex])

  const activateProgram = (idx) => {
    if (idx < 0 || idx >= programs.length) return
    setActiveIndex(idx)
  }

  const goProgramLeft = () => activateProgram(activeIndex - 1)
  const goProgramRight = () => activateProgram(activeIndex + 1)

  const goCoursesLeft = () => setCoursePage((p) => Math.max(0, p - 1))
  const goCoursesRight = () => setCoursePage((p) => Math.min(maxCoursePage, p + 1))

  if (!programs.length) {
    return (
      <section className="section_block" style={{ overflow: 'hidden' }}>
        <div className="specialisations">
          <div className="container text-center pt-5">
            <h2 className="title mb-1">Programs and Specializations</h2>
            <p className="text-muted mt-3">No program data found.</p>
          </div>
        </div>
      </section>
    )
  }

  return (
    <section className="section_block" style={{ overflow: 'hidden' }}>
      <div className="specialisations">
        <div className="container text-center pt-5">
          <h2 className="title mb-1">Programmes and Specialisations</h2>
        </div>

        <div className="container-fluid" style={{ padding: '30px 15px' }}>
          <div className="container d-flex align-items-center mb-3">
            <button
              type="button"
              className="btn-scroll me-2"
              onClick={goProgramLeft}
              disabled={activeIndex === 0}
              aria-label="Previous program"
            >
              &larr;
            </button>

            <div
              ref={scrollContainerRef}
              className="d-flex flex-nowrap gap-4 program-scroll"
              style={{ scrollBehavior: 'smooth', width: '100%' }}
            >
              {programs.map((prog, idx) => (
                <button
                  key={prog}
                  ref={(el) => (programBtnRefs.current[idx] = el)}
                  type="button"
                  className={`programe-btn px-4 py-2 ${idx === activeIndex ? 'active' : ''}`}
                  onClick={() => activateProgram(idx)}
                >
                  {prog}
                </button>
              ))}
            </div>

            <button
              type="button"
              className="btn-scroll ms-2"
              onClick={goProgramRight}
              disabled={activeIndex === programs.length - 1}
              aria-label="Next program"
            >
              &rarr;
            </button>
          </div>

          <div className="container-fluid pb-4 overflow-hidden box">
            <div className="text-center mb-4 p-2">
              <h5 className="fw-bold courses-name">{activeProgram?.title}</h5>
              <p className="mb-0 courses-duration">{activeProgram?.duration}</p>
            </div>

            <div className="w-100">
              <div className="row g-1 mx-0 w-100 justify-content-center">
                {visibleCourses.map((course, i) => (
                  <div
                    key={`${course.name}-${i}`}
                    className="col-6 col-md-3 col-lg-2 col-xl-2 d-flex justify-content-center align-items-center"
                  >
                    <div className="course-card h-100 w-100 d-flex flex-column align-items-center py-3">
                      <a href={course.url} target="_blank" rel="noreferrer">
                        <img className="course-img mb-2" src={course.img} alt={course.name} />
                      </a>

                      <a
                        href={course.url}
                        className="course-title text-center mb-0"
                        target="_blank"
                        rel="noreferrer"
                      >
                        {course.name}
                      </a>

                      {course.partName ? (
                        <small className="text-muted text-center mt-2">{course.partName}</small>
                      ) : null}
                    </div>
                  </div>
                ))}

                {!visibleCourses.length && (
                  <div className="text-muted text-center py-4">No courses found.</div>
                )}
              </div>
            </div>
          </div>

          <div className="d-flex justify-content-center align-items-center gap-2 my-2">
            <button
              type="button"
              className="course-scroll-btn me-2"
              onClick={goCoursesLeft}
              disabled={coursePage === 0}
              aria-label="Previous courses"
            >
              &lt;
            </button>

            <button
              type="button"
              className="course-scroll-btn ms-2"
              onClick={goCoursesRight}
              disabled={coursePage >= maxCoursePage}
              aria-label="Next courses"
            >
              &gt;
            </button>
          </div>
        </div>
      </div>
    </section>
  )
}

export default ProgramsSpecializations
